/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, } from "react";
import clsx, { type ClassValue, } from "clsx";
import { twMerge, } from "tailwind-merge";
import Select from "./Select";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, "size"> {
  label?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  fullWidth?: boolean;
  multiline?: boolean;
  variant?: "outlined" | "standard" | "filled";
  color?: "primary" | "secondary";
  rows?: number;
  select?: boolean; // Prop que ativa o modo Select
  SelectProps?: any; // Props adicionais para o componente Select
  size?: "small" | "medium";
  margin?: "none" | "dense" | "normal";
  minRows?: number;
  maxRows?: number;
  InputProps?: any;
  inputProps?: any;
  InputLabelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  InputSelectProps?: React.SelectHTMLAttributes<HTMLSelectElement>;
  defaultValue?: any;
}

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const {
    label,
    helperText,
    error = false,
    fullWidth = false,
    multiline = false,
    select = false,
    color = "primary",
    className,
    id,
    disabled,
    required,
    rows,
    children, // Opções do select
    onFocus,
    onBlur,
    InputProps,
    inputProps,
    InputSelectProps,
    InputLabelProps,
    defaultValue, // Desestruturado aqui
    ...other
  } = props;

  const [focused, setFocused] = useState(false);

  const handleFocus = (e: any) => {
    setFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e: any) => {
    setFocused(false);
    if (onBlur) onBlur(e);
  };

  const getLabelColor = () => {
    if (error) return "text-red-600";
    if (focused) return color === "primary" ? "text-primary" : "text-secondary";

    return "text-slate-500";
  };

  // Renderização condicional baseada na prop 'select' ou 'multiline'
  const renderInput = () => {
    // Aceita a convenção do MUI (`inputComponent`), usada em toda a aplicação para
    // máscaras (CpfMask, CellphoneMask, etc.). Esses componentes esperam `inputRef`
    // (callback) para entregar o elemento de input ao react-hook-form.
    const InputComponent = InputProps?.inputComponent;

    if (InputComponent) {
      const { className: inputClassName, ...restInputProps } =
        (InputProps ?? {}) as { className?: string } & Record<string, unknown>;
      const { className: innerInputClassName, ...restInnerInputProps } =
        (inputProps ?? {}) as { className?: string } & Record<string, unknown>;

      return (
        <div className="relative">
          <InputComponent
            inputRef={ref}
            id={id}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            defaultValue={defaultValue} // Repassando defaultValue
            className={cn(
              "w-full px-3 py-2.5 bg-white border rounded transition-all duration-200 outline-none ring-offset-0",
              "placeholder:text-slate-400 text-slate-900",
              // Lógica de bordas
              error ? "border-red-600 ring-red-600" :
                focused ? (color === "primary" ? "border-primary ring-primary" : "border-secondary ring-secondary") :
                  "border-slate-300 hover:border-slate-900",
              focused && "ring-1",
              disabled && "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed",
              inputClassName,
              innerInputClassName
            )}
            {...{ ...other, ...restInputProps, ...restInnerInputProps }}
          />
        </div>
      );
    }

    if (select) {
      return (
        <Select
          ref={ref}
          id={id}
          disabled={disabled}
          error={error}
          color={color}
          onFocus={handleFocus}
          onBlur={handleBlur}
          defaultValue={defaultValue} // Repassando defaultValue
          className="border-none p-0 focus:ring-0" // Removemos estilos extras pois o Select já os tem
          {...({ ...other, ...InputSelectProps } as any)}
        >
          {children}
        </Select>
      );
    }

    const InputElement = multiline ? "textarea" : "input";

    const { startAdornment, endAdornment, ref: inputPropsRef, ...restInputProps } = (InputProps ?? {}) as any;
    const hasAdornment = !multiline && (startAdornment || endAdornment);

    if (hasAdornment) {
      return (
        <div
          ref={inputPropsRef}
          className={cn(
            "relative flex items-center w-full px-3 py-2 bg-white border rounded transition-all duration-200 outline-none ring-offset-0",
            error ? "border-red-600 ring-red-600" :
              focused ? (color === "primary" ? "border-primary ring-primary" : "border-secondary ring-secondary") :
                "border-slate-300 hover:border-slate-900",
            focused && "ring-1",
            disabled && "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"
          )}
        >
          {startAdornment}
          <InputElement
            ref={ref}
            id={id}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            rows={rows}
            defaultValue={defaultValue} // Repassando defaultValue
            className={cn(
              "flex-1 min-w-0 bg-transparent outline-none border-0 py-0.5 primary-tx",
              "placeholder:text-slate-400",
              disabled && "text-slate-400 cursor-not-allowed"
            )}
            {...({ ...other, ...restInputProps, ...inputProps } as any)}
          />
          {endAdornment}
        </div>
      );
    }

    return (
      <div className="relative">
        <InputElement
          ref={ref as any}
          id={id}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          rows={rows}
          defaultValue={defaultValue} // Repassando defaultValue
          className={cn(
            "w-full px-3 py-2.5 bg-white border rounded transition-all duration-200 outline-none ring-offset-0",
            "placeholder:text-slate-400 primary-tx",
            // Lógica de bordas
            error ? "border-red-600 ring-red-600" :
              focused ? (color === "primary" ? "border-primary ring-primary" : "border-secondary ring-secondary") :
                "border-slate-300 hover:border-slate-900",
            focused && "ring-1",
            disabled && "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed",
            multiline && "resize-none"
          )}
          {...({ ...other, ...restInputProps, ...inputProps } as any)}
        />
      </div>
    );
  };

  return (
    <div className={cn("inline-flex flex-col relative", fullWidth && "w-full", className)}>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "block text-sm font-medium mb-1.5 transition-colors duration-200",
            getLabelColor(),
            disabled && "text-slate-400"
          )}
          {...InputLabelProps}
        >
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {renderInput()}

      {helperText && (
        <p className={cn(
          "mt-1.5 text-xs",
          error ? "text-red-600" : "secondary-tx",
          disabled && "text-slate-400"
        )}>
          {helperText}
        </p>
      )}
    </div>
  );
});

TextField.displayName = "TextField";

export default TextField;